/**
 * Custom Lightweight Rich Text Renderer
 *
 * Replaces @contentful/rich-text-react-renderer and @contentful/rich-text-html-renderer
 * with a minimal implementation supporting the node types used in UAG content:
 * - document (root)
 * - paragraph
 * - text (with marks: bold, italic, underline, code)
 * - unordered-list
 * - list-item
 * - heading-1 through heading-6
 * - hyperlink
 * - embedded-asset-block (PDFs and images)
 *
 * Bundle size: ~10 KB vs 643 KB (98% reduction)
 */

import ImageHelper from "@components/ImageHelper"
import React, { ReactNode } from "react"

/**
 * Options for customizing rendering
 */
export interface RenderOptions {
  renderNode?: {
    [nodeType: string]: (node: RichTextNode, children?: ReactNode) => ReactNode
  }
}

export interface RichTextDocument extends RichTextNode {
  content: RichTextNode[]
  nodeType: "document"
}

// Minimal type definitions (no full schema needed)
export interface RichTextNode {
  content?: RichTextNode[]
  data?: Record<string, any>
  marks?: Array<{ type: string }>
  nodeType: string
  value?: string
}

/**
 * Type guard to check if value is a valid rich text document
 */
export function isRichTextDocument(value: unknown): value is RichTextDocument {
  if (!value || typeof value !== "object") return false
  const v = value as Partial<RichTextDocument>
  return v.nodeType === "document" && Array.isArray(v.content)
}

/**
 * Render rich text to HTML string
 * Used by parseContentfulRichText.ts replacement
 */
export function renderToHtml(doc: null | RichTextDocument | undefined): string {
  if (!doc || !doc.content) return ""

  return doc.content.map(nodeToHtml).join("")
}

/**
 * Render rich text to React components
 * Used by gatsby-source-contentful's renderRichText replacement
 */
export function renderToReact(
  doc: null | RichTextDocument | undefined,
  options?: RenderOptions,
  references?: any[] | null
): ReactNode {
  if (!doc || !doc.content) return null

  // Create a reference lookup map for quick access
  // Note: We use contentful_id as the key because that's what matches sys.id in the raw data
  const refMap = new Map()
  if (references) {
    references.forEach((ref) => {
      if (ref?.contentful_id) {
        refMap.set(ref.contentful_id, ref)
      }
    })
  }

  return (
    <>{doc.content.map((node, i) => renderNode(node, i, options, refMap))}</>
  )
}

// Basic HTML escaping for security
function escapeHtml(str: string): string {
  const div =
    typeof document !== "undefined" ? document.createElement("div") : null
  if (div) {
    div.textContent = str
    return div.innerHTML
  }
  // Fallback for SSR
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
}

function inlineNodesToHtml(nodes: RichTextNode[] | undefined): string {
  if (!nodes || nodes.length === 0) return ""
  return nodes
    .map((node) => {
      if (node.nodeType === "text") {
        return textNodeToHtml(node)
      }
      return nodeToHtml(node)
    })
    .join("")
}

function nodeToHtml(node: RichTextNode): string {
  if (!node) return ""

  switch (node.nodeType) {
    case "document":
      return node.content?.map(nodeToHtml).join("") || ""

    case "embedded-asset-block": {
      const asset = node.data?.target
      if (!asset) return ""
      const url = asset.fields?.file?.url || asset.file?.url
      const description = asset.fields?.description || asset.description || ""
      if (!url) return ""
      return `<img src="https://${url}" alt="${escapeHtml(description)}" />`
    }

    case "heading-1":
      return `<h1>${inlineNodesToHtml(node.content)}</h1>`

    case "heading-2":
      return `<h2>${inlineNodesToHtml(node.content)}</h2>`

    case "heading-3":
      return `<h3>${inlineNodesToHtml(node.content)}</h3>`

    case "heading-4":
      return `<h4>${inlineNodesToHtml(node.content)}</h4>`

    case "heading-5":
      return `<h5>${inlineNodesToHtml(node.content)}</h5>`

    case "heading-6":
      return `<h6>${inlineNodesToHtml(node.content)}</h6>`

    case "hyperlink": {
      const url = node.data?.uri || "#"
      return `<a href="${escapeHtml(url)}">${inlineNodesToHtml(node.content)}</a>`
    }

    case "list-item":
      return `<li>${inlineNodesToHtml(node.content)}</li>`

    case "paragraph":
      return `<p>${inlineNodesToHtml(node.content)}</p>`

    case "text":
      return textNodeToHtml(node)

    case "unordered-list":
      return `<ul>${node.content?.map(nodeToHtml).join("") || ""}</ul>`

    default:
      console.warn(`Unknown rich text node type: ${node.nodeType}`)
      return ""
  }
}

function renderInlineNodes(
  nodes: RichTextNode[] | undefined,
  options?: RenderOptions,
  refMap?: Map<string, any>,
  parentKey?: string
): ReactNode {
  if (!nodes || nodes.length === 0) return null

  // Map inline nodes - the hierarchical keys ensure uniqueness
  return nodes.map((node, i) => {
    const nodeKey = parentKey ? `${parentKey}-inline-${i}` : `inline-${i}`
    if (node.nodeType === "text") {
      return renderTextNode(node, nodeKey)
    }
    return renderNode(node, nodeKey, options, refMap)
  })
}

function renderNode(
  node: RichTextNode,
  key: number | string,
  options?: RenderOptions,
  refMap?: Map<string, any>
): ReactNode {
  if (!node) return null

  // Check if there's a custom renderer for this node type
  if (options?.renderNode?.[node.nodeType]) {
    const children = renderInlineNodes(node.content, options, refMap, `${key}`)
    const customElement = options.renderNode[node.nodeType](node, children)

    // If the custom renderer returned an element, clone it with the key prop
    if (React.isValidElement(customElement)) {
      return React.cloneElement(customElement, { key } as any)
    }

    return customElement
  }

  switch (node.nodeType) {
    case "document":
      return (
        <React.Fragment key={key}>
          {node.content?.map((child, i) =>
            renderNode(child, `${key}-${i}`, options, refMap)
          )}
        </React.Fragment>
      )

    case "embedded-asset-block": {
      // First try to resolve from references using sys.id
      let asset = node.data?.target
      const assetId = asset?.sys?.id

      // If we have a reference map and asset ID, look up the full asset data
      if (assetId && refMap) {
        const resolvedAsset = refMap.get(assetId)
        if (resolvedAsset) {
          asset = resolvedAsset
        }
      }

      if (!asset) return null

      // Get URL from either direct asset or resolved reference
      const url = asset.file?.url || asset.fields?.file?.url

      if (!url) return null

      // Check if it's a PDF
      const isPdf = url.indexOf(".pdf") !== -1
      const title = asset.title || asset.fields?.title || "Download"
      const description =
        asset.description || asset.fields?.description || title

      if (isPdf) {
        return (
          <p key={key}>
            <a href={`https:${url}`} rel="noopener noreferrer" target="_blank">
              {title}
            </a>
          </p>
        )
      }

      return <ImageHelper alt={description} key={key} src={`https:${url}`} />
    }

    case "heading-1":
      return (
        <h1 key={key}>
          {renderInlineNodes(node.content, options, refMap, `${key}`)}
        </h1>
      )

    case "heading-2":
      return (
        <h2 key={key}>
          {renderInlineNodes(node.content, options, refMap, `${key}`)}
        </h2>
      )

    case "heading-3":
      return (
        <h3 key={key}>
          {renderInlineNodes(node.content, options, refMap, `${key}`)}
        </h3>
      )

    case "heading-4":
      return (
        <h4 key={key}>
          {renderInlineNodes(node.content, options, refMap, `${key}`)}
        </h4>
      )

    case "heading-5":
      return (
        <h5 key={key}>
          {renderInlineNodes(node.content, options, refMap, `${key}`)}
        </h5>
      )

    case "heading-6":
      return (
        <h6 key={key}>
          {renderInlineNodes(node.content, options, refMap, `${key}`)}
        </h6>
      )

    case "hyperlink": {
      const url = node.data?.uri || "#"
      return (
        <a href={url} key={key}>
          {renderInlineNodes(node.content, options, refMap, `${key}`)}
        </a>
      )
    }

    case "list-item":
      return (
        <li key={key}>
          {renderInlineNodes(node.content, options, refMap, `${key}`)}
        </li>
      )

    case "paragraph":
      return (
        <p key={key}>
          {renderInlineNodes(node.content, options, refMap, `${key}`)}
        </p>
      )

    case "text":
      return renderTextNode(node, key)

    case "unordered-list":
      return (
        <ul key={key}>
          {node.content?.map((child, i) =>
            renderNode(child, `${key}-${i}`, options, refMap)
          )}
        </ul>
      )

    default:
      // Graceful degradation for any unexpected node types
      console.warn(`Unknown rich text node type: ${node.nodeType}`)
      return null
  }
}

function renderTextNode(node: RichTextNode, key: number | string): ReactNode {
  let text = node.value || ""

  // Handle marks if they exist (currently none used, but futureproof)
  if (node.marks && node.marks.length > 0) {
    node.marks.forEach((mark) => {
      switch (mark.type) {
        case "bold":
          text = `<strong>${text}</strong>`
          break
        case "code":
          text = `<code>${text}</code>`
          break
        case "italic":
          text = `<em>${text}</em>`
          break
        case "underline":
          text = `<u>${text}</u>`
          break
      }
    })
    // If marks were applied, render as HTML
    if (node.marks.length > 0) {
      return <span dangerouslySetInnerHTML={{ __html: text }} key={key} />
    }
  }

  return <React.Fragment key={key}>{text}</React.Fragment>
}

function textNodeToHtml(node: RichTextNode): string {
  let text = escapeHtml(node.value || "")

  // Handle marks if they exist
  if (node.marks && node.marks.length > 0) {
    node.marks.forEach((mark) => {
      switch (mark.type) {
        case "bold":
          text = `<strong>${text}</strong>`
          break
        case "code":
          text = `<code>${text}</code>`
          break
        case "italic":
          text = `<em>${text}</em>`
          break
        case "underline":
          text = `<u>${text}</u>`
          break
      }
    })
  }

  return text
}
